// glossary.js — searchable reference of the terms used across E8 Studio's essays.
//
// Each entry is { term, short, long, related, group }. `short` is the one-line
// definition shown in the list; `long` is the fuller explanation shown when the
// entry is expanded. `related` is a list of other term ids (for cross-links).
// `group` controls the section header in the modal.
//
// Definitions are kept consistent with the essays in content/essays.js — no new
// mathematical claims that the essays don't already support.

export const GLOSSARY = [
  // ── Lie theory ──
  {
    id: 'root-system',
    term: 'Root system',
    group: 'Lie theory',
    short: 'A set of vectors in ℝⁿ that encodes a Lie algebra’s symmetry.',
    long: 'A root system is a configuration of vectors (the "roots") in a Euclidean space, invariant under reflection in the hyperplanes perpendicular to each root. For a complex semisimple Lie algebra, its root system determines its isomorphism type. E8’s root system is 240 vectors in ℝ⁸ — the structure this studio visualises.',
    related: ['simple-root', 'weyl-group', 'lie-algebra'],
  },
  {
    id: 'cartan-matrix',
    term: 'Cartan matrix',
    group: 'Lie theory',
    short: 'The 8×8 matrix of inner products of the simple roots.',
    long: 'The Cartan matrix encodes the angles between simple roots: each entry is 2⟨αᵢ,αⱼ⟩/⟨αⱼ,αⱼ⟩. For E8 it is an 8×8 matrix of 2s on the diagonal, −1s and 0s off it. Together with the Dynkin diagram it captures the full structure of the Lie algebra.',
    related: ['simple-root', 'dynkin-diagram'],
  },
  {
    id: 'weyl-group',
    term: 'Weyl group',
    group: 'Lie theory',
    short: 'The finite symmetry group generated by reflections in the roots.',
    long: 'The Weyl group W is generated by reflecting space across each root’s perpendicular hyperplane. For E8, |W(E8)| = 696,729,600 = 2¹⁴·3⁵·5²·7 — enormous, yet finite. It permutes the 240 roots among themselves.',
    related: ['root-system', 'coxeter-element', 'simple-root'],
  },
  {
    id: 'simple-root',
    term: 'Simple root',
    group: 'Lie theory',
    short: 'One of a minimal set of roots from which all others are built.',
    long: 'A choice of 8 "simple roots" α₁..α₈ is a basis such that every other root is a linear combination with all-positive or all-negative coefficients. E8 has rank 8, so 8 simple roots. Their pairwise angles are read off the Dynkin diagram.',
    related: ['root-system', 'cartan-matrix', 'dynkin-diagram', 'rank'],
  },
  {
    id: 'dynkin-diagram',
    term: 'Dynkin diagram',
    group: 'Lie theory',
    short: 'A graph that encodes the angles between simple roots.',
    long: 'Each node is a simple root; an edge means the roots are not orthogonal. For simply-laced groups like E8 every edge is single (120° angle). E8’s diagram is a chain of 7 nodes with one branch. The diagram classifies the complex simple Lie algebras (Aₙ, Bₙ, Cₙ, Dₙ, E₆, E₇, E₈, F₄, G₂).',
    related: ['simple-root', 'cartan-matrix', 'ade-classification'],
  },
  {
    id: 'coxeter-element',
    term: 'Coxeter element',
    group: 'Lie theory',
    short: 'The product of all simple reflections — a rotation of order h.',
    long: 'The Coxeter element c is the composition of one reflection per simple root. It acts as a rotation; its order is the Coxeter number h. For E8, h = 30, so c rotates the Coxeter plane by 2π/30 = 12°. The roots break into orbits of c, giving the 30-cycles seen in the app.',
    related: ['coxeter-number', 'coxeter-plane', 'weyl-group'],
  },
  {
    id: 'coxeter-number',
    term: 'Coxeter number',
    group: 'Lie theory',
    short: 'The order of the Coxeter element; for E8 it is 30.',
    long: 'h is the number of roots divided by the rank: 240/8 = 30 for E8. It equals the length of the Petrie polygon and the order of the Coxeter element’s rotation in the Coxeter plane. It controls how the roots partition into rings under the Coxeter projection.',
    related: ['coxeter-element', 'petrie-polygon', 'root-system'],
  },
  {
    id: 'coxeter-plane',
    term: 'Coxeter plane',
    group: 'Lie theory',
    short: 'A special 2D plane where the Coxeter element acts as a pure rotation.',
    long: 'The Coxeter plane is the 2D subspace of ℝ⁸ on which a Coxeter element acts as a rotation by 2π/h. Projecting the 240 E8 roots onto it arranges them into 8 concentric rings of 30 roots each.',
    related: ['coxeter-element', 'coxeter-number', 'root-system'],
  },
  {
    id: 'petrie-polygon',
    term: 'Petrie polygon',
    group: 'Lie theory',
    short: 'A skew edge-path where every two consecutive edges, but no three, share a face.',
    long: 'A Petrie polygon is a skew polygon along a polytope’s edges such that any two consecutive edges lie in a face, but no three do. In the E8 Coxeter-plane picture, a 30-step Coxeter orbit projects to 30 equally spaced points on one of the eight circles.',
    related: ['coxeter-number', 'coxeter-plane'],
  },
  {
    id: 'rank',
    term: 'Rank',
    group: 'Lie theory',
    short: 'The dimension of the Cartan subalgebra (= number of simple roots).',
    long: 'For a complex semisimple Lie algebra, rank is the dimension of a Cartan subalgebra, equivalently the number of simple roots. E8 has rank 8. Its Cartan subalgebra and 240 one-dimensional root spaces give dimension 8 + 240 = 248.',
    related: ['simple-root', 'lie-algebra'],
  },
  {
    id: 'lie-algebra',
    term: 'Lie algebra',
    group: 'Lie theory',
    short: 'A vector space of infinitesimal symmetries with a bracket product.',
    long: 'A Lie algebra is a vector space equipped with a bilinear, antisymmetric bracket [X,Y] satisfying the Jacobi identity. It is the infinitesimal version of a Lie group (a smooth symmetry group). E8 as a Lie algebra has dimension 248; as a Lie group it has infinitely many elements.',
    related: ['root-system', 'rank', 'exceptional-group'],
  },
  {
    id: 'exceptional-group',
    term: 'Exceptional Lie group',
    group: 'Lie theory',
    short: 'One of G₂, F₄, E₆, E₇, E₈ — the five that don’t fit an infinite family.',
    long: 'The complex simple Lie algebras fall into four infinite families (Aₙ, Bₙ, Cₙ, Dₙ) and five exceptional ones. E8 is the largest exception, with dimension 248. E8 × E8 also appears as one of the two gauge-group choices permitted by the ten-dimensional heterotic-string construction; that is a separate physics result.',
    related: ['lie-algebra', 'ade-classification'],
  },

  // ── Geometry ──
  {
    id: 'platonic-solid',
    term: 'Platonic solid',
    group: 'Geometry',
    short: 'One of the five convex regular polyhedra in 3D.',
    long: 'A Platonic solid has identical regular polygon faces meeting identically at every vertex. There are exactly five: tetrahedron, cube, octahedron, dodecahedron, icosahedron. Theaetetus proved the list complete (Euclid, Elements XIII).',
    related: ['schlafli-symbol', 'duality', 'stellation'],
  },
  {
    id: 'schlafli-symbol',
    term: 'Schläfli symbol',
    group: 'Geometry',
    short: 'Notation {p,q} classifying regular polytopes in any dimension.',
    long: '{p,q} means faces are regular p-gons, q meeting at each vertex. {4,3} = cube. {5/2,5} = small stellated dodecahedron (5/2 = pentagram face). In 4D: {3,3,5} = 600-cell. Ludwig Schläfli introduced this notation in 1852.',
    related: ['platonic-solid', 'regular-polytope', 'stellation'],
  },
  {
    id: 'duality',
    term: 'Duality',
    group: 'Geometry',
    short: 'Swapping faces and vertices of a polytope to get its dual.',
    long: 'The dual of a polyhedron swaps faces ↔ vertices across edges. Cube ↔ octahedron, dodecahedron ↔ icosahedron, tetrahedron ↔ itself. Schläfli symbols reverse: {p,q} dual is {q,p}.',
    related: ['platonic-solid', 'schlafli-symbol'],
  },
  {
    id: 'stellation',
    term: 'Stellation',
    group: 'Geometry',
    short: 'Extending a polyhedron’s faces until they meet again — making stars.',
    long: 'Stellation extends the faces of a polyhedron past its edges until the new faces intersect. The four Kepler–Poinsot star polyhedra are stellations of the dodecahedron and icosahedron. Same vertices as the convex solids; richer edge/face topology.',
    related: ['platonic-solid', 'schlafli-symbol'],
  },
  {
    id: 'golden-ratio',
    term: 'Golden ratio',
    group: 'Geometry',
    short: 'φ = (1+√5)/2 ≈ 1.618 — the proportion in pentagons and icosahedra.',
    long: 'φ appears in standard coordinates for the icosahedron, dodecahedron, and 600-cell because those objects have fivefold or icosahedral symmetry. The Studio uses that shared coordinate structure as a visual bridge; the McKay correspondence itself is representation-theoretic, not a statement that φ generates E8.',
    related: ['platonic-solid', 'binary-polyhedral-group'],
  },
  {
    id: 'regular-polytope',
    term: 'Regular polytope',
    group: 'Geometry',
    short: 'A shape with maximal symmetry — identical faces, edges, vertices.',
    long: 'A regular polytope generalises the Platonic solids to any dimension. In 4D there are exactly six convex regular polytopes (5-cell, tesseract, 16-cell, 24-cell, 600-cell, 120-cell), versus only five in 3D and infinitely many in 2D.',
    related: ['schlafli-symbol', 'sixhundred-cell', '24-cell'],
  },
  {
    id: 'sixhundred-cell',
    term: '600-cell',
    group: 'Geometry',
    short: 'The 4D regular polytope with 120 vertices and 720 edges.',
    long: 'The 600-cell {3,3,5} has 120 vertices, 720 edges, 1200 triangular faces, and 600 tetrahedral cells. Its 120 vertices can be identified with the unit quaternions of the binary icosahedral group. E8 also admits a relationship with H4 under a suitable projection, but two ordinary 600-cells in 4D are not literally the E8 root system in 8D.',
    related: ['regular-polytope', 'quaternion', 'binary-polyhedral-group', 'schlafli-symbol'],
  },
  {
    id: '24-cell',
    term: '24-cell',
    group: 'Geometry',
    short: 'A 4D regular polytope with 24 vertices — self-dual, no 3D analogue.',
    long: 'The 24-cell {3,4,3} has 24 vertices, 96 edges, 96 triangular faces. It is the only regular polytope that is self-dual AND has no analogue in any other dimension. Its vertices are the 24 roots of D4 inside E8.',
    related: ['regular-polytope', 'root-system'],
  },

  // ── Algebra ──
  {
    id: 'octonion',
    term: 'Octonions (𝕆)',
    group: 'Algebra',
    short: 'The 8-dimensional normed division algebra — non-associative.',
    long: 'The octonions are the largest of the four normed division algebras (ℝ, ℂ, ℍ, 𝕆). They are non-associative and participate in important constructions involving exceptional Lie groups. E8 has rank 8, but that numerical match alone is not a construction or explanation of E8.',
    related: ['quaternion', 'exceptional-group', 'lie-algebra'],
  },
  {
    id: 'quaternion',
    term: 'Quaternion (ℍ)',
    group: 'Algebra',
    short: 'The 4-dimensional normed division algebra; encodes 3D rotation.',
    long: 'A spatial rotation through angle θ about axis v̂ can be represented by the unit quaternion q = (cos(θ/2), sin(θ/2)·v̂), acting by conjugation; q and −q represent the same rotation. The 120 unit quaternions of the binary icosahedral group are the vertices of the 600-cell. Composing rotations corresponds to multiplying their representing quaternions.',
    related: ['octonion', 'sixhundred-cell', 'binary-polyhedral-group'],
  },
  {
    id: 'binary-polyhedral-group',
    term: 'Binary polyhedral group',
    group: 'Algebra',
    short: 'The double cover of a polyhedron’s rotation group, inside the quaternions.',
    long: 'The rotation group of a Platonic solid has a 2-to-1 "double cover" inside the unit quaternions. The binary icosahedral group (order 120) gives the 600-cell’s vertices. McKay observed these groups correspond to E6/E7/E8 — the McKay correspondence.',
    related: ['mckay-correspondence', 'quaternion', 'sixhundred-cell'],
  },
  {
    id: 'mckay-correspondence',
    term: 'McKay correspondence',
    group: 'Algebra',
    short: 'An association between finite SU(2) subgroups and affine ADE Dynkin diagrams.',
    long: 'John McKay’s observation associates finite subgroups of SU(2) with simply-laced affine Dynkin diagrams by forming a tensor-product graph from their irreducible representations. Binary tetrahedral → Ẽ₆, binary octahedral → Ẽ₇, and binary icosahedral → Ẽ₈. The Studio’s morph is an educational visual comparison, not the McKay construction itself.',
    related: ['binary-polyhedral-group', 'dynkin-diagram', 'ade-classification'],
  },
  {
    id: 'ade-classification',
    term: 'ADE classification',
    group: 'Algebra',
    short: 'The simply-laced Lie algebras: Aₙ, Dₙ, E₆, E₇, E₈.',
    long: 'The simply-laced Dynkin diagrams (all single edges) split into the A series (Aₙ), D series (Dₙ), and the three exceptions E₆, E₇, E₈. McKay’s correspondence attaches each binary polyhedral group to one of these. The A and D families are in dynkin.json but only E6/E7/E8 are surfaced in this studio so far.',
    related: ['dynkin-diagram', 'mckay-correspondence', 'exceptional-group'],
  },
  {
    id: 'weyl-vector',
    term: 'Weyl vector',
    group: 'Lie theory',
    short: 'The half-sum of positive roots — a distinguished point in weight space.',
    long: 'ρ = ½ Σ(positive roots). It sits at the centre of the Weyl chamber and satisfies ⟨ρ, αᵢ∨⟩ = 1 for every simple coroot. It appears in the Weyl character formula and in the geometry of the root polytope.',
    related: ['root-system', 'simple-root', 'weyl-group'],
  },
];

const GLOSSARY_GROUP_PROVENANCE = {
  'Lie theory': ['aim-e8-technical', 'stembridge-coxeter-planes'],
  Geometry: ['mathworld-platonic-solids', 'mathworld-schlafli-symbol', 'mathworld-600-cell'],
  Algebra: ['mckay-michigan-notes', 'baez-octonions'],
};

for (const entry of GLOSSARY) {
  entry.claimType = 'established-mathematics';
  entry.sourceIds = GLOSSARY_GROUP_PROVENANCE[entry.group] || [];
  entry.scopeNote = `Reference definition in ${entry.group}; Studio-specific visual comparisons are explanatory.`;
}

// Group order (controls display order in the modal).
export const GLOSSARY_GROUPS = ['Lie theory', 'Geometry', 'Algebra'];

// Substring search across term + short + long. Empty query returns everything.
// Matches on the term itself are ranked first (so typing "coxeter" surfaces the
// Coxeter terms before the others that merely mention Coxeter).
export function getGlossaryMatches(query = '') {
  const q = String(query).trim().toLowerCase();
  if (!q) return GLOSSARY;
  const matches = [];
  for (const entry of GLOSSARY) {
    const term = entry.term.toLowerCase();
    const short = entry.short.toLowerCase();
    const long = entry.long.toLowerCase();
    if (term.includes(q) || short.includes(q) || long.includes(q)) {
      const rank = term.includes(q) ? 0 : short.includes(q) ? 1 : 2;
      matches.push({ entry, rank });
    }
  }
  matches.sort((a, b) => a.rank - b.rank || a.entry.term.localeCompare(b.entry.term));
  return matches.map(m => m.entry);
}

export function getGlossaryEntry(id) {
  return GLOSSARY.find(e => e.id === id) || null;
}
